import {Collection, Media} from "../../types/types";
import {getPublicUrl} from "../../common/helpers";
import {ThumbnailContainer, Thumbnail} from "./Thumbnail";

import {Box, Typography, useTheme} from '@mui/material';
import Container from "@mui/material/Container";
import Grid from '@mui/material/Unstable_Grid2';
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Video from "yet-another-react-lightbox/plugins/video";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import React from "react";
import FileDownloadMenu from "./FileDownloadMenu";
import Description from "./Description";


interface ImageListProps {
    collection: Collection,
    locale: string,
}

const supportedLightboxMimeTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
];

const supportedVideoMimeTypes = [
    "video/mp4",
];

const getFirstPlayableVideoAsset = (media: Media | undefined) => {
    if (!media) {
        return null;
    }

    return Object.values(media)
        .filter(item => item.fileType && supportedVideoMimeTypes.indexOf(item.fileType) !== -1).at(0)
}

const placeholderUrl = "https://placehold.co/600x400?text=Image%20not%20available";

const ImageList = ({collection, locale}: ImageListProps) => {

    const [isLightboxOpen, setLightboxOpen] = React.useState(false);
    const [clickedImageIndex, setClickedImageIndex] = React.useState(0);
    const images = collection.assets.map(item => {
        return {
            src: getPublicUrl(item.media!["thumbnail"]!.path!, true),
            title: item.metadata?.simple?.title,
            credit: item.metadata?.simple?.credit,
            description: item.metadata?.translatable![locale]?.description,
            media: item.media,
            isVideo: item.isVideo,
            isPlayableVideo: item.isVideo && !!getFirstPlayableVideoAsset(item.media)
        }
    });

    const lightboxImages = React.useMemo<any[]>(() => {
        return collection.assets.map(item => {


            const supportedImages = "downloadPreview" in item.media! ?
                [item.media["downloadPreview"]] :
                Object.values(item.media!)
                    .filter(item => supportedLightboxMimeTypes.indexOf(item.fileType!) !== -1)
                    .sort((a, b) =>
                        ((a.imageHeight || 1) * (a.imageWidth || 1)) >= ((b.imageHeight || 1) * (b.imageWidth || 1)) ? 1 : -1);

            const descriptionData = {
                title: <Box>
                    {item.metadata?.simple?.title}
                    <FileDownloadMenu isInOverlay={true} locale={locale} media={item.media}/>
                </Box>
            };

            if (item.isVideo) {
                const videoAsset = getFirstPlayableVideoAsset(item.media);
                if (videoAsset) {
                    return {
                        ...descriptionData,
                        ...{
                            type: "video",
                            poster: supportedImages.length > 0 ? getPublicUrl(supportedImages.at(-1)!.path!) : placeholderUrl,
                            sources: [
                                {
                                    src: getPublicUrl(videoAsset!.path!, true),
                                    type: videoAsset!.fileType,
                                }
                            ]
                        }
                    }
                }
            }

            if (supportedImages.length) {
                // add the last image and then the remaining ones as srcSet
                return {
                    ...descriptionData,
                    ...{
                        src: getPublicUrl(supportedImages.at(-1)!.path!, true),
                        width: supportedImages.at(-1)!.imageWidth!,
                        height: supportedImages.at(-1)!.imageHeight!,
                        srcSet: supportedImages.map(item => ({
                            src: getPublicUrl(item.path!, true),
                            width: item.imageWidth!,
                            height: item.imageHeight!,
                        }))
                    }
                };
            } else {
                // add only the placeholder image
                return {
                    src: placeholderUrl,
                    width: 600,
                    height: 400,
                };
            }
        });
    }, [collection, locale]);

    const theme = useTheme();

    const openLightbox = (index: number) => {
        setClickedImageIndex(index);
        setLightboxOpen(true);
    }

    return <Container maxWidth="xl" sx={{py: "1rem"}}>

        <Grid container spacing={{xs: 2, md: 3}} columns={{xs: 4, sm: 8, md: 12}}>
            {images.map((item, index) => (
                <Grid display="flex" xs={4} sm={4} md={4} lg={3} key={index}>
                    <ThumbnailContainer>
                        <Thumbnail
                            alt={item.title}
                            loading="lazy"
                            onClick={() => openLightbox(index)}
                            src={item.src}
                            srcSet={item.src}
                            isVideo={item.isPlayableVideo || false}
                        />
                        <Box sx={{
                            pt: theme.spacing(1),
                            display: "flex",
                            justifyContent: "space-between",
                            overflowWrap: "anywhere"
                        }}>
                            <Typography variant="h6" gutterBottom>{item.title}</Typography>
                            <FileDownloadMenu locale={locale} media={item.media}/>
                        </Box>
                        <Description content={item.description} locale={locale}/>
                        {!!item.credit && <Typography variant="subtitle2" gutterBottom
                                                      sx={{fontSize: "0.7rem", pt: theme.spacing(1), mt: "auto"}}>
                            Credit: {item.credit}
                        </Typography>}
                    </ThumbnailContainer>
                </Grid>
            ))}
        </Grid>
        <Lightbox
            open={isLightboxOpen}
            controller={{closeOnBackdropClick: true}}
            close={() => setLightboxOpen(false)}
            index={clickedImageIndex}
            plugins={[Captions, Video]}
            slides={lightboxImages}
            styles={{container: {backgroundColor: "rgba(0, 0, 0, .85)"}}}
            video={{autoPlay: true}}
        />

    </Container>
}

export default ImageList;